(() => {
  if (customElements.get('related-products-carousel')) return;

  const dragThreshold = 8;
  const verticalWheelResetDelay = 160;

  class RelatedProductsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-related-products-track]');
      if (!this.track) return;

      this.initialized = true;
      this.measureFrame = null;
      this.clickResetTimer = null;
      this.verticalWheelTimer = null;
      this.position = this.track.scrollLeft;
      this.maxScroll = 0;
      this.stepWidth = 0;
      this.pointerDown = false;
      this.dragging = false;
      this.suppressNextClick = false;
      this.startX = 0;
      this.startY = 0;
      this.lastX = 0;

      this.bindEvents();
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.track);
      Array.from(this.track.children).forEach((item) => this.resizeObserver.observe(item));
      this.scheduleMeasure();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      window.cancelAnimationFrame(this.measureFrame);
      window.clearTimeout(this.clickResetTimer);
      window.clearTimeout(this.verticalWheelTimer);
      this.resizeObserver?.disconnect();
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('wheel', this.onWheel);
      this.track.removeEventListener('keydown', this.onKeyDown);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('click', this.onClickCapture, true);
      this.track.removeEventListener('dragstart', this.onDragStart);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.initialized = false;
    }

    bindEvents() {
      this.onScroll = () => {
        if (!this.pointerDown) this.position = this.clamp(this.track.scrollLeft);
      };
      this.onWheel = (event) => this.handleWheel(event);
      this.onKeyDown = (event) => this.handleKeyDown(event);
      this.onPointerDown = (event) => this.handlePointerDown(event);
      this.onPointerMove = (event) => this.handlePointerMove(event);
      this.onPointerUp = (event) => this.handlePointerUp(event);
      this.onClickCapture = (event) => this.handleClickCapture(event);
      this.onDragStart = (event) => event.preventDefault();

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('wheel', this.onWheel, { passive: false });
      this.track.addEventListener('keydown', this.onKeyDown);
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', this.onClickCapture, true);
      this.track.addEventListener('dragstart', this.onDragStart);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    scheduleMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = window.requestAnimationFrame(() => {
        this.measureFrame = null;
        this.updateMeasurements();
      });
    }

    updateMeasurements() {
      const firstItem = this.track.firstElementChild;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;

      this.maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      this.stepWidth = firstItem ? firstItem.getBoundingClientRect().width + gap : 0;
      this.setPosition(this.track.scrollLeft);
    }

    clamp(position) {
      return Math.max(0, Math.min(position, this.maxScroll));
    }

    setPosition(position) {
      this.position = this.clamp(position);
      this.track.scrollLeft = this.position;
    }

    handleWheel(event) {
      const absDeltaX = Math.abs(event.deltaX);
      const absDeltaY = Math.abs(event.deltaY);
      const isHorizontal = absDeltaX > absDeltaY || (event.shiftKey && absDeltaY > 0);
      if (!isHorizontal) {
        this.track.classList.add('is-vertical-wheeling');
        window.clearTimeout(this.verticalWheelTimer);
        this.verticalWheelTimer = window.setTimeout(() => {
          this.track.classList.remove('is-vertical-wheeling');
        }, verticalWheelResetDelay);
        return;
      }

      const delta = absDeltaX > absDeltaY ? event.deltaX : event.deltaY;
      if (!delta) return;

      event.preventDefault();
      this.setPosition(this.position + delta);
    }

    handleKeyDown(event) {
      if (event.target !== this.track) return;

      let nextPosition;
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') nextPosition = this.position - this.stepWidth;
      if (event.key === 'ArrowRight' || event.key === 'PageDown') nextPosition = this.position + this.stepWidth;
      if (event.key === 'Home') nextPosition = 0;
      if (event.key === 'End') nextPosition = this.maxScroll;
      if (nextPosition === undefined) return;

      event.preventDefault();
      this.setPosition(nextPosition);
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.pointerType !== 'mouse') return;

      this.pointerDown = true;
      this.dragging = false;
      this.suppressNextClick = false;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.lastX = event.clientX;
      this.position = this.clamp(this.track.scrollLeft);
    }

    handlePointerMove(event) {
      if (!this.pointerDown) return;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (!this.dragging) {
        if (absDeltaX < dragThreshold && absDeltaY < dragThreshold) return;
        if (absDeltaY >= absDeltaX) return;

        this.dragging = true;
        this.suppressNextClick = true;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();
      const movement = event.clientX - this.lastX;
      this.setPosition(this.position - movement);
      this.lastX = event.clientX;
    }

    handlePointerUp(event) {
      if (!this.pointerDown) return;

      this.pointerDown = false;
      if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      this.track.classList.remove('is-dragging');
      window.clearTimeout(this.clickResetTimer);
      this.clickResetTimer = window.setTimeout(() => {
        this.suppressNextClick = false;
      }, 400);
    }

    handleClickCapture(event) {
      if (!this.suppressNextClick) return;

      event.preventDefault();
      event.stopPropagation();
      window.clearTimeout(this.clickResetTimer);
      this.suppressNextClick = false;
    }
  }

  customElements.define('related-products-carousel', RelatedProductsCarousel);
})();
